import React from 'react';
import { useTranslation } from 'react-i18next';
import ShopingLists from '@features/shopping-list/add-product/ProductAddToShoppingList.tsx';
import Counter from '@components/forms/Counter.tsx';
import { PriceVariantDTO } from '@api/generated/search-service';
import { formatMoney, formatPercentage, formatUnit } from '@utils/formats.ts';
import PriceTypeComponent from '@features/pricetypes/PriceTypeComponent.tsx';
import { isNullOrUndefined } from '@utils/utils.tsx';

interface ProductPricingProps {
  priceVariant: PriceVariantDTO;
  productId: number;
  vat: number;
  minAmount: number;
  onlyMultiply: boolean;
  onAddToCart: (quantity: number) => void;
  onDeleteCartItem: () => void;
  quantityInCart?: number;
  isExpired?: boolean;
}

const ProductPricing: React.FC<ProductPricingProps> = ({
  onAddToCart,
  priceVariant,
  onDeleteCartItem,
  quantityInCart,
  vat,
  minAmount,
  onlyMultiply,
  productId,
  isExpired,
}) => {
  const { t } = useTranslation('client');
  if (isNullOrUndefined(priceVariant)) return <>{t('productDetail.noPrice')}</>;

  return (
    <div className="w-full h-full">
      {/* Info message - nedostupný produkt */}
      {isExpired && (
        <div data-icon="true" data-purpose="Error" data-size="Default" className="w-full p-2 bg-[#da1e28] rounded flex justify-start items-start gap-2 mb-4">
          <div data-svg-wrapper data-style="Outlined" className="relative">
            <svg width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path
                d="M9 4.8675L14.6475 14.625H3.3525L9 4.8675ZM9 1.875L0.75 16.125H17.25L9 1.875ZM9.75 12.375H8.25V13.875H9.75V12.375ZM9.75 7.875H8.25V10.875H9.75V7.875Z"
                fill="white"
              />
            </svg>
          </div>
          <div className="flex-1 justify-center text-white text-[13px] font-medium font-brand leading-[18px]">{t('productDetail.productUnavailable')}</div>
        </div>
      )}

      {/* Hlavní cena */}
      <div className="mb-6">
        <div className="flex justify-between gap-4">
          <span className="text-gray-600 text-base font-medium">{t('productDetail.price')}</span>
          <div className="flex items-center gap-2">
            <span className="text-xl font-bold text-gray-900">
              {formatMoney(priceVariant.currentPrice)}/{formatUnit(priceVariant.priceUnit)}
            </span>
          </div>
        </div>

        <div className="flex flex-col gap-1 text-sm text-gray-500">
          <div className="flex justify-between gap-2">
            <span className="font-medium">{t('productDetail.pricePerUnit', { unit: formatUnit(priceVariant.priceUnit) })}</span>
            <span>{formatMoney(priceVariant.packageItemPrice)}</span>
          </div>
          <div className="flex justify-between gap-2">
            <span className="font-medium">{t('productDetail.vatRate')}</span>
            <span>{formatPercentage(vat)}</span>
          </div>
        </div>
        <div className="flex flex-col gap-1 text-sm text-gray-500">
          <div className="flex justify-between items-center gap-2">
            <span className="font-medium">{t('productDetail.priceType')}</span>
            <PriceTypeComponent priceType={priceVariant.priceType} isGaranted={priceVariant.isGaranted} size="sm" />
          </div>
        </div>
      </div>

      {/* Tlačítko do košíku */}
      <div className={`my-2 flex flex-row justify-start ${isExpired ? 'w-full' : 'max-w-[200px]'}`}>
        {isExpired ? (
          <button
            disabled
            className="w-full px-4 py-2 rounded-full bg-controls-surface-light-disabled text-controls-text-light-disabled font-medium text-sm cursor-not-allowed"
          >
            {t('productDetail.cannotBuy')}
          </button>
        ) : (
          <Counter
            variant="button"
            quantity={quantityInCart}
            minimumQuantity={minAmount}
            unit={priceVariant.priceUnit}
            onlyMultiply={onlyMultiply}
            onQuantityChange={newQuantity => onAddToCart(newQuantity)}
            onRemove={() => onDeleteCartItem()}
            disabled={false}
          />
        )}
      </div>

      {/* Seznamy */}
      {priceVariant && priceVariant.id && (
        <div className={'mb-4'}>
          <ShopingLists priceVariantId={priceVariant.id} productId={productId} />
        </div>
      )}
    </div>
  );
};

export default ProductPricing;
